import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from 'react-helmet';
import Header from "./Header";
import Footer from "./Footer";
import FloatingButton from "./FloatingButton";
import Topsection from "./Topsection";
import { FaCalendarAlt, FaUser, FaTags } from "react-icons/fa";

const slugify = (text) =>
  text.toLowerCase().replace(/[^\w ]+/g, '').replace(/ +/g, '-');

const BlogDetail = () => {
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { slug } = useParams();

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE}/blogs`);
        if (!response.ok) throw new Error('Failed to fetch blog');
        const data = await response.json();
        const foundBlog = data.find(b => slugify(b.title) === slug);
        if (!foundBlog) throw new Error('Blog not found');
        setBlog(foundBlog);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchBlog();
  }, [slug]);

  // Function to process content HTML and modify image tags
  const processContent = (html) => {
    if (!html) return html;
    
    // Create a temporary div to parse the HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Find all images and add classes
    const images = tempDiv.querySelectorAll('img');
    images.forEach(img => {
      // Add default class for all content images
      img.classList.add('content-image');
      
      // Check if image has existing alignment classes
      if (!img.classList.contains('img-left') && 
          !img.classList.contains('img-right') &&
          !img.classList.contains('img-center')) {
        // Add center alignment by default if no alignment class exists
        img.classList.add('img-center');
      }
    });
    
    return tempDiv.innerHTML;
  };

  if (loading) {
    return (
      <div className="blog-loading">
        <div className="blog-spinner"></div>
        <p>Loading blog post...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="blog-error">
        <h2>Oops!</h2>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="blog-detail-page">
      <Helmet>
        <title>{blog.title} | Fortune Cloud Technologies</title>
        <meta name="description" content={blog.content.substring(0, 160) + "..."} />
        <meta name="keywords" content={blog.keywords?.join(", ") || ""} />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <Topsection />
      <Header />
      
      <main className="blog-main-container">
        <article className="blog-article-card">
          <header className="blog-article-header">
            <div className="blog-meta-info">
              <span className="blog-meta-item">
                <FaUser className="meta-icon" /> {blog.author}
              </span>
              <span className="blog-meta-item">
                <FaCalendarAlt className="meta-icon" /> {blog.date}
              </span>
              {blog.keywords?.length > 0 && (
                <span className="blog-meta-item">
                  <FaTags className="meta-icon" /> {blog.keywords.join(", ")}
                </span>
              )}
            </div>
            <h1 className="blog-title">{blog.title}</h1>
          </header>

          <div className="blog-featured-image-wrapper">
            <img
              src={`${process.env.REACT_APP_API_BASE}/uploads/blog/${blog.image}`}
              alt={blog.title}
              className="blog-featured-image"
              loading="lazy"
            />
          </div>

          <div 
            className="blog-content"
            dangerouslySetInnerHTML={{ __html: processContent(blog.content) }}
          />

          {blog.keywords?.length > 0 && (
            <footer className="blog-tags-footer">
              <div className="blog-tags-container">
                {blog.keywords.map((keyword, index) => (
                  <span key={index} className="blog-tag">{keyword}</span>
                ))}
              </div>
            </footer>
          )}
        </article>
      </main>

      <Footer />
      <FloatingButton />
    </div>
  );
};

export default BlogDetail;